import {
    ChangeEvent,
    DetailedHTMLProps,
    forwardRef,
    ForwardRefExoticComponent,
    InputHTMLAttributes,
    ReactElement,
    RefAttributes,
    useContext,
    useEffect,
    useState,
} from 'react'

import { getRandomUuid } from '../../helpers/crypto'
import CheckboxGroupContext from './CheckboxGroupContext'

type NoneCheckerCheckboxProps = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & RefAttributes<HTMLInputElement>, 'checked'>

const NoneCheckerCheckbox: ForwardRefExoticComponent<NoneCheckerCheckboxProps> = forwardRef((props, ref): ReactElement => {
    const {
        disabled,
        onChange,
    } = props

    const [id] = useState(getRandomUuid())
    const checkboxGroup = useContext(CheckboxGroupContext)

    const [initialized, setInitialized] = useState(false)
    const [shouldTriggerCheckboxContextChange, setShouldTriggerCheckboxContextChange] = useState<boolean>(true)
    const [isChecked, setIsChecked] = useState<boolean|undefined>(checkboxGroup.defaultChecked !== undefined ? ! checkboxGroup.defaultChecked : undefined)
    const [isDisabled, setIsDisabled] = useState(disabled !== undefined ? disabled : checkboxGroup.defaultDisabled)

    useEffect((): () => void => {
        checkboxGroup.assertIdDoesNotExist(id)
        return (): void => {
            checkboxGroup.noneCheckerCheckboxes.delete(id)
        }
    }, [])

    useEffect((): void => {
        checkboxGroup.noneCheckerCheckboxes.set(id, {
            isChecked,
            isDisabled,
            props,
            setIsChecked,
            setIsDisabled,
        })

        if (shouldTriggerCheckboxContextChange) {
            checkboxGroup.onNoneCheckerCheckboxChange(id, initialized)
            setShouldTriggerCheckboxContextChange(false)
        }

        if (! initialized) {
            setInitialized(true)
        }
    }, [
        id, isChecked, isDisabled, setIsChecked, setIsDisabled, initialized,
        setShouldTriggerCheckboxContextChange, checkboxGroup, shouldTriggerCheckboxContextChange,
    ])

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        event.persist()

        if (! isDisabled) {
            setShouldTriggerCheckboxContextChange(true)
            setIsChecked(event.target.checked)
        }

        if (onChange !== undefined) {
            onChange(event)
        }
    }

    return (
        <input
            type="checkbox"
            {...props}
            ref={ref}
            onChange={handleChange}
            checked={isChecked !== undefined ? isChecked : false}
            disabled={isDisabled !== undefined ? isDisabled : false}
        />
    )
})

export default NoneCheckerCheckbox
